import React, { useState, useEffect } from "react";
import axios from "../../../../axios-order";
import BoxWrapper from "../../../../components/ui/boxWrapper";
import Appbar from "../../../../components/appBar/appBar";

const pad = (n) => String(n).padStart(2, "0");
const toApiDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const toDisplayDate = (isoStr) => {
    if (!isoStr) return "—";
    const d = new Date(isoStr);
    return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const defaultTo = () => toApiDate(new Date());
const defaultFrom = () => { const d = new Date(); d.setDate(d.getDate() - 6); return toApiDate(d); };

const GST_TYPES = ["GSTR1", "GSTR1A", "GSTR3B"];
const TDS_TYPES = ["24Q", "26Q", "27Q"];

const STATUS_COLOR = (status) => {
    const s = status?.toLowerCase();
    if (["pass", "success", "succeeded"].includes(s)) return { bg: "#e6f4ea", color: "#1e7e34", dot: "#1e7e34" };
    if (["fail", "failed", "error"].includes(s)) return { bg: "#fdecea", color: "#c0392b", dot: "#c0392b" };
    return { bg: "#fff8e1", color: "#b8860b", dot: "#f0ad4e" };
};

/* ─── inline styles ─────────────────────────────────────────────── */
const S = {
    page: {
        margin: "24px 28px",
        marginTop: 76,
        fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
        color: "#1e293b",
    },
    headerBar: {
        background: "linear-gradient(100deg, #03406d 0%, #0a5a99 100%)",
        borderRadius: 12,
        padding: "0 28px",
        marginBottom: 16,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        height: 68,
        boxShadow: "0 4px 20px rgba(3,64,109,0.22)",
    },
    headerLeft: {
        display: "flex",
        alignItems: "center",
        gap: 14,
    },
    headerIconWrap: {
        width: 38,
        height: 38,
        borderRadius: 9,
        background: "rgba(255,255,255,0.14)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 18,
    },
    headerEyebrow: {
        fontSize: 10,
        fontWeight: 600,
        color: "rgba(255,255,255,0.55)",
        letterSpacing: 1.2,
        textTransform: "uppercase",
        marginBottom: 2,
    },
    headerTitle: {
        fontSize: 17,
        fontWeight: 700,
        color: "#fff",
        lineHeight: 1,
    },
    tabGroup: {
        display: "flex",
        background: "rgba(0,0,0,0.2)",
        borderRadius: 8,
        padding: 3,
        gap: 2,
    },
    tabBtn: (active) => ({
        padding: "6px 28px",
        border: "none",
        borderRadius: 6,
        background: active ? "#fff" : "transparent",
        color: active ? "#03406d" : "rgba(255,255,255,0.75)",
        fontWeight: 700,
        fontSize: 12,
        cursor: "pointer",
        letterSpacing: 0.5,
        transition: "all 0.18s",
        boxShadow: active ? "0 2px 8px rgba(0,0,0,0.18)" : "none",
    }),
    filterRow: {
        background: "#fff",
        borderRadius: 10,
        padding: "14px 20px",
        marginBottom: 14,
        display: "flex",
        alignItems: "flex-end",
        gap: 14,
        flexWrap: "wrap",
        boxShadow: "0 1px 5px rgba(0,0,0,0.06)",
        border: "1px solid #e2e8f0",
    },
    filterLabel: {
        fontSize: 10,
        fontWeight: 700,
        color: "#94a3b8",
        textTransform: "uppercase",
        letterSpacing: 0.8,
        marginBottom: 5,
        display: "block",
    },
    filterInput: {
        border: "1.5px solid #e2e8f0",
        borderRadius: 7,
        padding: "7px 12px",
        fontSize: 13,
        color: "#1e293b",
        height: 36,
        outline: "none",
        background: "#f8fafc",
        transition: "border-color 0.2s",
        fontFamily: "inherit",
    },
    searchBtn: (loading) => ({
        height: 36,
        padding: "0 22px",
        borderRadius: 7,
        border: "none",
        background: loading ? "#7aa8cc" : "#03406d",
        color: "#fff",
        fontSize: 12,
        fontWeight: 700,
        cursor: loading ? "not-allowed" : "pointer",
        letterSpacing: 0.4,
        display: "flex",
        alignItems: "center",
        gap: 6,
        transition: "background 0.18s",
        fontFamily: "inherit",
    }),
    filterEntityWrap: {
        marginLeft: "auto",
        position: "relative",
    },
    filterEntityInput: {
        border: "1.5px solid #e2e8f0",
        borderRadius: 7,
        padding: "7px 12px 7px 32px",
        fontSize: 13,
        color: "#1e293b",
        height: 36,
        width: 220,
        outline: "none",
        background: "#f8fafc",
        fontFamily: "inherit",
    },
    filterEntityIcon: {
        position: "absolute",
        left: 9,
        bottom: 9,
        fontSize: 14,
        color: "#94a3b8",
    },
    /* ── summary cards (original style) ── */
    summaryRow: {
        display: "flex",
        gap: 14,
        marginBottom: 14,
        flexWrap: "wrap",
    },
    summaryCard: (bg, accent, wide) => ({
        background: bg,
        border: `1.5px solid ${accent}22`,
        borderRadius: 10,
        padding: "14px 20px",
        minWidth: wide ? 210 : 130,
        boxShadow: "0 1px 5px rgba(0,0,0,0.06)",
        display: "flex",
        alignItems: "center",
        gap: 12,
    }),
    summaryValue: (color, small) => ({
        fontSize: small ? 14 : 24,
        fontWeight: 700,
        color,
        lineHeight: 1.1,
    }),
    summaryLabel: {
        fontSize: 11,
        color: "#666",
        marginTop: 3,
        textTransform: "uppercase",
        letterSpacing: 0.5,
    },
    /* table */
    tableCard: {
        background: "#fff",
        borderRadius: 10,
        overflow: "hidden",
        boxShadow: "0 1px 8px rgba(0,0,0,0.07)",
        border: "1px solid #e2e8f0",
    },
    emptyState: {
        textAlign: "center",
        padding: "64px 0",
        color: "#94a3b8",
    },
    emptyIcon: { fontSize: 36, marginBottom: 12 },
    emptyText: { fontSize: 14, fontWeight: 500 },
    th: {
        padding: "11px 14px",
        fontSize: 10,
        fontWeight: 700,
        color: "#fff",
        textAlign: "left",
        letterSpacing: 0.8,
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        background: "linear-gradient(90deg, #03406d, #0a5a99)",
        borderBottom: "none",
    },
    td: { padding: "10px 14px", fontSize: 13, verticalAlign: "middle" },
    expandBtn: (active, hasFiled) => ({
        width: 24,
        height: 24,
        borderRadius: "50%",
        border: `1.5px solid ${active ? "#03406d" : hasFiled ? "#b0c8e0" : "#dde4eb"}`,
        background: active ? "#03406d" : "#fff",
        color: active ? "#fff" : hasFiled ? "#03406d" : "#c0c8d2",
        cursor: hasFiled ? "pointer" : "default",
        fontSize: 14,
        fontWeight: 800,
        lineHeight: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "all 0.18s",
        boxShadow: active ? "0 2px 6px rgba(3,64,109,0.25)" : "none",
    }),
    filedPill: {
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        background: "#e6f4ea",
        color: "#1a7e44",
        border: "1px solid #a8d5b5",
        borderRadius: 20,
        padding: "3px 11px",
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.2,
    },
    notFiledPill: {
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        background: "#fdecea",
        color: "#c0392b",
        border: "1px solid #f5b7b1",
        borderRadius: 20,
        padding: "3px 11px",
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.2,
    },
    pillDot: (color) => ({ width: 6, height: 6, borderRadius: "50%", background: color }),
    expandedPanel: {
        padding: "16px 24px 16px 56px",
        background: "#f0f6ff",
        borderBottom: "2px solid #c8ddf5",
    },
    expandedTitle: {
        fontSize: 10,
        fontWeight: 700,
        color: "#03406d",
        textTransform: "uppercase",
        letterSpacing: 0.8,
        marginBottom: 10,
    },
    innerTh: {
        padding: "8px 14px",
        fontSize: 10,
        fontWeight: 700,
        color: "#03406d",
        textAlign: "left",
        textTransform: "uppercase",
        letterSpacing: 0.5,
        background: "#d0e5f7",
    },
    filingTypeBadge: {
        background: "#ddeeff",
        color: "#03406d",
        borderRadius: 5,
        padding: "3px 10px",
        fontSize: 11,
        fontWeight: 700,
    },
    paginationWrap: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "12px 20px",
        borderTop: "1px solid #e8eef4",
        background: "#fff",
    },
    paginationInfo: { fontSize: 12, color: "#94a3b8" },
    pageBtn: (disabled, active) => ({
        minWidth: 30,
        height: 30,
        borderRadius: 6,
        border: `1px solid ${active ? "#03406d" : "#e2e8f0"}`,
        background: active ? "#03406d" : disabled ? "#f8fafc" : "#fff",
        color: active ? "#fff" : disabled ? "#cbd5e1" : "#475569",
        fontSize: 12,
        cursor: disabled ? "default" : "pointer",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "0 8px",
        fontWeight: active ? 700 : 400,
        transition: "all 0.15s",
        fontFamily: "inherit",
    }),
};

function TrackFilingStatus() {
    const [activeTab, setActiveTab] = useState("GST");
    const [fromDate, setFromDate] = useState(defaultFrom());
    const [toDate, setToDate] = useState(defaultTo());
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dateRange, setDateRange] = useState(null);
    const [fetched, setFetched] = useState(false);
    const [expandedRows, setExpandedRows] = useState({});
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [caFirmEntities, setCaFirmEntities] = useState([]);
    const [selectedCaFirmEntity, setSelectedCaFirmEntity] = useState("");
    const [caFirmEntityName, setCaFirmEntityName] = useState("");

    useEffect(() => {
        const business_entity_id = parseInt(localStorage.getItem("business_mgmt_entity_id"));
        axios.post("/ca_firm_entity_list", { business_entity_id })
            .then((res) => { setCaFirmEntities(res.data.records || res.data || []); })
            .catch(() => setCaFirmEntities([]));
    }, []);

    const fetchData = () => {
        if (!fromDate || !toDate) return;
        setLoading(true);
        setExpandedRows({});
        axios.get(`/filing_status/${fromDate}/${toDate}`, {
            params: { ca_firm_entity_id: selectedCaFirmEntity || undefined },
        })
            .then((res) => {
                setRecords(res.data.records || []);
                const dr = res.data.date_range || null;
                setDateRange(dr);
                if (dr?.from) setFromDate(dr.from);
                if (dr?.to) setToDate(dr.to);
                setFetched(true);
            })
            .catch(() => { setRecords([]); setFetched(true); })
            .finally(() => setLoading(false));
    };

    useEffect(() => { fetchData(); }, []); // eslint-disable-line
    useEffect(() => { setCurrentPage(1); }, [search, activeTab]);

    const handleCaFirmEntityChange = (e) => {
        const selectedEntityId = e.target.value;
        const selectedEntity = caFirmEntities.find((entity) => entity.business_entity_id == selectedEntityId);
        setSelectedCaFirmEntity(selectedEntityId);
        setCaFirmEntityName(selectedEntity ? selectedEntity.entity_name : "");
    };

    const toggleRow = (id) => setExpandedRows((p) => ({ ...p, [id]: !p[id] }));
    const handleTabSwitch = (tab) => { setActiveTab(tab); setExpandedRows({}); };

    const types = activeTab === "GST" ? GST_TYPES : TDS_TYPES;

    const filteredRecords = records.filter((r) => {
        const q = search.toLowerCase();
        return !q || r.entity_name?.toLowerCase().includes(q) || r.gstin?.toLowerCase().includes(q) || r.pan?.toLowerCase().includes(q);
    });

    const filedCount = filteredRecords.filter((r) => r.filings?.some((f) => types.includes(f.filing_type?.toUpperCase()))).length;
    const notFiledCount = filteredRecords.length - filedCount;

    const rowsPerPage = 10;
    const totalPages = Math.ceil(filteredRecords.length / rowsPerPage);
    const paginatedRecords = filteredRecords.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

    return (
        <>
            <Appbar />
            <div style={S.page}>

                {/* ── Header ── */}
                <div style={S.headerBar}>
                    <div style={S.headerLeft}>
                        <div style={S.headerIconWrap}>📋</div>
                        <div>
                            <div style={S.headerEyebrow}>Admin Dashboard</div>
                            <div style={S.headerTitle}>Track Filing Status</div>
                        </div>
                    </div>
                    <div style={S.tabGroup}>
                        {["GST", "TDS"].map((tab) => (
                            <button key={tab} onClick={() => handleTabSwitch(tab)} style={S.tabBtn(activeTab === tab)}>
                                {tab}
                            </button>
                        ))}
                    </div>
                </div>

                {/* ── Filters ── */}
                <div style={S.filterRow}>
                    <div>
                        <label style={S.filterLabel}>From Date</label>
                        <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} max={toDate}
                            style={S.filterInput} />
                    </div>
                    <div>
                        <label style={S.filterLabel}>To Date</label>
                        <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} min={fromDate}
                            style={S.filterInput} />
                    </div>
                    <div>
                        <label style={S.filterLabel}>CA Firm</label>
                        <select value={selectedCaFirmEntity} onChange={handleCaFirmEntityChange}
                            style={{ ...S.filterInput, minWidth: 190, cursor: "pointer" }}>
                            <option value="">Select</option>
                            {caFirmEntities.map((entity) => (
                                <option key={entity.business_entity_id} value={entity.business_entity_id}>
                                    {entity.entity_name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <button onClick={fetchData} disabled={loading} style={S.searchBtn(loading)}>
                        <span>🔍</span>
                        <span>{loading ? "Searching…" : "Search"}</span>
                    </button>
                    <div style={S.filterEntityWrap}>
                        <label style={S.filterLabel}>Filter Entities</label>
                        <input type="text" placeholder="Search name, GSTIN, PAN…" value={search}
                            onChange={(e) => setSearch(e.target.value)} style={S.filterEntityInput} />
                        <span style={S.filterEntityIcon}>⌕</span>
                    </div>
                </div>

                {/* ── Summary Cards (original layout, refined) ── */}
                {fetched && (
                    <div style={S.summaryRow}>
                        {/* Total Entities */}
                        <div style={S.summaryCard("#eaf2fb", "#03406d", false)}>
                            <span style={{ fontSize: 22 }}>🏢</span>
                            <div>
                                <div style={S.summaryValue("#03406d", false)}>{filteredRecords.length}</div>
                                <div style={S.summaryLabel}>Total Entities</div>
                            </div>
                        </div>
                        {/* Filed */}
                        <div style={S.summaryCard("#e6f4ea", "#1a7e44", false)}>
                            <span style={{ fontSize: 22 }}>✅</span>
                            <div>
                                <div style={S.summaryValue("#1a7e44", false)}>{filedCount}</div>
                                <div style={S.summaryLabel}>{activeTab} Filed</div>
                            </div>
                        </div>
                        {/* Date Range */}
                        {dateRange && (
                            <div style={S.summaryCard("#fdf3e7", "#92600a", true)}>
                                <span style={{ fontSize: 22 }}>📅</span>
                                <div>
                                    <div style={S.summaryValue("#92600a", true)}>
                                        {dateRange.from} → {dateRange.to}
                                    </div>
                                    <div style={S.summaryLabel}>Date Range</div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* ── Table ── */}
                <div style={S.tableCard}>
                    {loading ? (
                        <div style={S.emptyState}>
                            <div style={S.emptyIcon}>⏳</div>
                            <div style={S.emptyText}>Loading filing data…</div>
                        </div>
                    ) : fetched && filteredRecords.length === 0 ? (
                        <div style={S.emptyState}>
                            <div style={S.emptyIcon}>📭</div>
                            <div style={S.emptyText}>No records found for the selected filters.</div>
                        </div>
                    ) : fetched ? (
                        <>
                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                <thead>
                                    <tr>
                                        <th style={{ ...S.th, width: 48, textAlign: "center" }}></th>
                                        <th style={{ ...S.th, width: 48 }}>#</th>
                                        <th style={S.th}>Entity Name</th>
                                        <th style={S.th}>GSTIN</th>
                                        <th style={S.th}>PAN</th>
                                        <th style={S.th}>Branch</th>
                                        <th style={{ ...S.th, textAlign: "center" }}>Filing Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedRecords.map((r, i) => {
                                        const globalIndex = (currentPage - 1) * rowsPerPage + i;
                                        const isExpanded = !!expandedRows[r.entity_id];
                                        const relevantFilings = (r.filings || []).filter((f) => types.includes(f.filing_type?.toUpperCase()));
                                        const hasFiled = relevantFilings.length > 0;
                                        const rowBg = isExpanded ? "#edf5ff" : i % 2 === 0 ? "#fff" : "#f8fafc";

                                        return (
                                            <React.Fragment key={r.entity_id}>
                                                <tr style={{ background: rowBg, borderBottom: "1px solid #f1f5f9", transition: "background 0.12s" }}>
                                                    <td style={{ ...S.td, textAlign: "center" }}>
                                                        <button
                                                            onClick={() => toggleRow(r.entity_id)}
                                                            title={isExpanded ? "Hide Details" : "View Details"}
                                                            style={S.expandBtn(isExpanded, hasFiled)}>
                                                            {isExpanded ? "−" : "+"}
                                                        </button>
                                                    </td>
                                                    <td style={{ ...S.td, color: "#94a3b8", fontWeight: 600, fontSize: 12 }}>{globalIndex + 1}</td>
                                                    <td style={{ ...S.td, fontWeight: 700, color: "#0f172a" }}>{r.entity_name}</td>
                                                    <td style={{ ...S.td, fontFamily: "monospace", fontSize: 12, color: "#475569", letterSpacing: 0.3 }}>{r.gstin || "—"}</td>
                                                    <td style={{ ...S.td, fontFamily: "monospace", fontSize: 12, color: "#475569" }}>{r.pan || "—"}</td>
                                                    <td style={{ ...S.td, color: "#64748b" }}>{r.branch || "—"}</td>
                                                    <td style={{ ...S.td, textAlign: "center" }}>
                                                        {hasFiled ? (
                                                            <span style={S.filedPill}>
                                                                <span style={S.pillDot("#1a7e44")} />
                                                                {relevantFilings.length} Filing{relevantFilings.length > 1 ? "s" : ""}
                                                            </span>
                                                        ) : (
                                                            <span style={S.notFiledPill}>
                                                                <span style={S.pillDot("#c0392b")} />
                                                                Not Filed
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>

                                                {isExpanded && (
                                                    <tr>
                                                        <td colSpan={7} style={{ padding: 0 }}>
                                                            <div style={S.expandedPanel}>
                                                                <div style={S.expandedTitle}>
                                                                    {activeTab} Filing History — {r.entity_name}
                                                                </div>
                                                                {relevantFilings.length === 0 ? (
                                                                    <div style={{ color: "#94a3b8", fontSize: 13 }}>
                                                                        No {activeTab} filings found in this date range.
                                                                    </div>
                                                                ) : (
                                                                    <table style={{ width: "100%", borderCollapse: "collapse", borderRadius: 8, overflow: "hidden" }}>
                                                                        <thead>
                                                                            <tr>
                                                                                {["#", "Filing Type", "Filing Date & Time", "Status"].map((h) => (
                                                                                    <th key={h} style={S.innerTh}>{h}</th>
                                                                                ))}
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {relevantFilings.map((f, fi) => {
                                                                                const sc = STATUS_COLOR(f.status);
                                                                                return (
                                                                                    <tr key={fi} style={{ background: fi % 2 === 0 ? "#fff" : "#f4f9ff", borderBottom: "1px solid #e0ecf8" }}>
                                                                                        <td style={{ padding: "8px 14px", fontSize: 12, color: "#94a3b8", fontWeight: 600 }}>{fi + 1}</td>
                                                                                        <td style={{ padding: "8px 14px" }}>
                                                                                            <span style={S.filingTypeBadge}>{f.filing_type}</span>
                                                                                        </td>
                                                                                        <td style={{ padding: "8px 14px", fontSize: 12, color: "#475569", fontFamily: "monospace" }}>
                                                                                            {toDisplayDate(f.filing_date)}
                                                                                        </td>
                                                                                        <td style={{ padding: "8px 14px" }}>
                                                                                            <span style={{
                                                                                                display: "inline-flex", alignItems: "center", gap: 5,
                                                                                                background: sc.bg, color: sc.color,
                                                                                                borderRadius: 5, padding: "3px 10px", fontSize: 11, fontWeight: 700,
                                                                                            }}>
                                                                                                <span style={{ width: 6, height: 6, borderRadius: "50%", background: sc.dot }} />
                                                                                                {f.status}
                                                                                            </span>
                                                                                        </td>
                                                                                    </tr>
                                                                                );
                                                                            })}
                                                                        </tbody>
                                                                    </table>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>

                            {totalPages > 1 && (
                                <div style={S.paginationWrap}>
                                    <div style={S.paginationInfo}>
                                        Showing{" "}
                                        <strong style={{ color: "#1e293b" }}>{(currentPage - 1) * rowsPerPage + 1}</strong>–
                                        <strong style={{ color: "#1e293b" }}>{Math.min(currentPage * rowsPerPage, filteredRecords.length)}</strong>{" "}
                                        of <strong style={{ color: "#1e293b" }}>{filteredRecords.length}</strong> entities
                                    </div>
                                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                        <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} style={S.pageBtn(currentPage === 1, false)}>«</button>
                                        <button onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1} style={S.pageBtn(currentPage === 1, false)}>‹</button>
                                        {Array.from({ length: totalPages }, (_, idx) => idx + 1)
                                            .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                                            .reduce((acc, p, i, arr) => {
                                                if (i > 0 && p - arr[i - 1] > 1) acc.push("...");
                                                acc.push(p);
                                                return acc;
                                            }, [])
                                            .map((p, idx) => p === "..." ? (
                                                <span key={`e${idx}`} style={{ padding: "0 4px", color: "#94a3b8", fontSize: 12 }}>…</span>
                                            ) : (
                                                <button key={p} onClick={() => setCurrentPage(p)} style={S.pageBtn(false, currentPage === p)}>{p}</button>
                                            ))}
                                        <button onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages} style={S.pageBtn(currentPage === totalPages, false)}>›</button>
                                        <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} style={S.pageBtn(currentPage === totalPages, false)}>»</button>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : null}
                </div>
            </div>
        </>
    );
}

export default TrackFilingStatus;